import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import Counter from 'LuxKit/Counter'
import TextButton from 'LuxKit/Button/TextButton'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripStop, TripDay } from 'tripPlanner/api/types'
import { doesStayExceedTripDates } from 'tripPlanner/utils/chatItinerary'
import { getDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import ExtendTripModal from './ExtendTripModal'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

const FieldGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const NightsFieldGroup = styled(Group)`
  flex: 0 0 auto;
  width: max-content;
  max-width: 100%;
`

const DaySelectButton = styled(TextButton)`
  justify-content: space-between;
`

const StayCounter = styled(Counter)`
  width: max-content;
  min-width: ${rem(140)};
  max-width: 100%;
  justify-content: space-between;
`

interface DayOption {
  dayNumber: number
  date?: string
  cityName: string
}

function buildDayOptionsForStop(stop: TripStop): Array<DayOption> {
  const options = stop.days.map((day: TripDay) => ({
    dayNumber: day.dayNumber,
    date: day.date,
    cityName: stop.placeName,
  }))

  if (options.length === 0) {
    return [{ dayNumber: 1, cityName: stop.placeName }]
  }

  return options
}

interface Props {
  isOpen: boolean
  hotelName: string
  relevantStop?: TripStop
  defaultDayNumber?: number
  defaultNights?: number
  /** Offer minimum length of stay; nights counter cannot go below this. */
  minNights?: number
  onClose: () => void
  onSubmit: (message: string) => void
}

function AddHotelToTripModal({
  isOpen,
  hotelName,
  relevantStop,
  defaultDayNumber = 1,
  defaultNights = 1,
  minNights = 1,
  onClose,
  onSubmit,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()

  // No matched stop: nights-only flow. Do not invent a "new stop" from hotelCity —
  // the agent should decide stop placement without FE guessing.
  const isUnmatchedStop = !relevantStop
  const targetStop = relevantStop
  const nightsFloor = minNights > 0 ? minNights : 1
  const initialNights = Math.max(defaultNights, nightsFloor)
  const showMinimumStayNotice = nightsFloor > 1
  const minimumStayNotice = showMinimumStayNotice
    ? intl.formatMessage(
        {
          id: '_mMZ6kBcn_63lNZdbzTp',
          defaultMessage: 'Minimum stay for this offer is {minStay, plural, one {# night} other {# nights}}',
          description: 'Subtitle showing minimum stay duration for the offer',
        },
        { minStay: nightsFloor },
      )
    : undefined

  const dayOptions = useMemo(
    () => !isUnmatchedStop && targetStop ? buildDayOptionsForStop(targetStop) : [],
    [isUnmatchedStop, targetStop],
  )

  const [selectedDay, setSelectedDay] = useState<DayOption | undefined>(() =>
    dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0],
  )
  const [nights, setNights] = useState(initialNights)

  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false)
  const [extendState, setExtendState] = useState<{ isOpen: boolean; extraDays: number }>({
    isOpen: false,
    extraDays: 0,
  })

  const dayButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(isOpen)

  // Modal stays mounted while closed (isOpen only). Re-seed from props when it
  // opens so async offer min-nights are picked up, without overwriting the
  // user's counter while the modal is already open.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setSelectedDay(dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0])
    setNights(Math.max(defaultNights, nightsFloor))
    setIsDayDropdownOpen(false)
    setExtendState({ isOpen: false, extraDays: 0 })
  }, [isOpen, dayOptions, defaultDayNumber, defaultNights, nightsFloor])

  // If min nights rises while open (e.g. offer finishes loading), raise the
  // counter to the floor without resetting a longer user selection.
  useEffect(() => {
    if (!isOpen) return
    setNights(prev => Math.max(prev, nightsFloor))
  }, [isOpen, nightsFloor])

  const handleDayDropdownOpen = useCallback(() => {
    setIsDayDropdownOpen(true)
  }, [])

  const handleDayDropdownClose = useCallback(() => {
    setIsDayDropdownOpen(false)
  }, [])

  const handleDaySelect = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const dayNumber = Number(e.currentTarget.dataset.dayNumber)
    if (isNaN(dayNumber)) return

    const option = dayOptions.find(d => d.dayNumber === dayNumber)
    if (option) {
      setSelectedDay(option)
    }
    setIsDayDropdownOpen(false)
  }, [dayOptions])

  const handleNightsChange = useCallback((value: number) => {
    setNights(Math.max(value, nightsFloor))
  }, [nightsFloor])

  const buildMessage = useCallback(() => {
    if (isUnmatchedStop) {
      const displayMessage = intl.formatMessage(
        {
          id: '9a483gOas6Nysi1NmYXI',
          defaultMessage: 'Add "{hotelName}" for {nights, plural, one {# night} other {# nights}}',
          description: 'User chat message shown when adding a hotel for a stay length without a matched trip stop',
        },
        { hotelName, nights },
      )
      return appendContextTag(
        displayMessage,
        `Add "${hotelName}" for ${nights} nights`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      )
    }

    if (!selectedDay) return undefined
    const endDay = selectedDay.dayNumber + nights
    const fromDisplayLabel = getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date)
    const fromContextLabel = getDayLabel(selectedDay.dayNumber, selectedDay.date)
    const toDate = selectedDay.date ? moment(selectedDay.date).add(nights, 'days').format('YYYY-MM-DD') : undefined
    const toDisplayLabel = getLocalisedDayLabel(intl, endDay, toDate)
    const toContextLabel = getDayLabel(endDay, toDate)
    const displayMessage = intl.formatMessage(
      {
        id: '05Xk8n84P0XUpCggECPh',
        defaultMessage: 'Assign "{hotelName}" to stay from {fromLabel} to {toLabel} in {cityName}',
        description: 'User chat message shown when assigning a hotel stay to a date range in a specific city',
      },
      {
        hotelName,
        fromLabel: fromDisplayLabel,
        toLabel: toDisplayLabel,
        cityName: formatPlacePhrase({ location: { name: selectedDay.cityName }, language: intl.locale, intent: 'location' }),
      },
    )

    return appendContextTag(
      displayMessage,
      `Assign "${hotelName}" to stay from ${fromContextLabel} to ${toContextLabel} in ${selectedDay.cityName}`,
      TRIP_PLANNER_ACTION_CONTEXT_LABEL,
    )
  }, [hotelName, isUnmatchedStop, intl, selectedDay, nights])

  const handleUpdate = useCallback(() => {
    if (!isUnmatchedStop && !selectedDay) return

    if (!isUnmatchedStop && targetStop && selectedDay) {
      const { exceeds, extraDays } = doesStayExceedTripDates(selectedDay.dayNumber, nights, targetStop)
      if (exceeds) {
        setExtendState({ isOpen: true, extraDays })
        return
      }
    }

    const message = buildMessage()
    if (message) {
      onSubmit(message)
    }
  }, [isUnmatchedStop, selectedDay, targetStop, nights, buildMessage, onSubmit])

  const handleExtendConfirm = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
    const message = buildMessage()
    if (message) {
      onSubmit(message)
    }
  }, [buildMessage, onSubmit])

  const handleExtendClose = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
  }, [])

  // Modal stays mounted when closed; reset so the next open starts clean even
  // if open-transition seeding is skipped (e.g. rapid cancel).
  const handleClose = useCallback(() => {
    setSelectedDay(dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0])
    setNights(Math.max(defaultNights, nightsFloor))
    setIsDayDropdownOpen(false)
    setExtendState({ isOpen: false, extraDays: 0 })
    onClose()
  }, [onClose, dayOptions, defaultDayNumber, defaultNights, nightsFloor])

  const nightsFormatter = useCallback((value: number) => {
    return intl.formatMessage(
      {
        id: '0Y7CYLTTLQVHQr1XuL12',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Night count for hotel trip assignment duration selector',
      },
      { count: value },
    )
  }, [intl])

  return (
    <>
      <Modal
        isOpen={isOpen && !extendState.isOpen}
        onClose={handleClose}
        title={intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)}
        subtitle={hotelName}
        primaryActionText={intl.formatMessage({
          id: 'uCjTDWMBDm9S499NCV7s',
          defaultMessage: 'Update',
          description: 'Primary action button to update hotel assignment in trip',
        })}
        onPrimaryActionClick={handleUpdate}
        isPrimaryActionDisabled={(!isUnmatchedStop && !selectedDay) || isStreaming}
        secondaryActionText={intl.formatMessage({
          id: 't0-oeWZUrkVajvX2QR3v',
          defaultMessage: 'Cancel',
          description: 'Secondary action button to cancel adding hotel to trip',
        })}
        onSecondaryActionClick={handleClose}
      >
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" gap={24}>
            {!isUnmatchedStop && (
              <FieldGroup direction="vertical" gap={8}>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'dBVesFs_nfMyF8ADPi0T',
                    defaultMessage: 'Check-in',
                    description: 'Label for the check-in day selection field when adding a hotel to trip',
                  })}
                </BodyText>
                <DaySelectButton
                  ref={dayButtonRef}
                  kind="secondary"
                  size="large"
                  endIcon={<LineAngleDownIcon />}
                  onClick={handleDayDropdownOpen}
                  fit="full-width"
                >
                  {selectedDay ? getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date) : intl.formatMessage({
                    id: 'f6OvnZs4BqTTYR5XNvmx',
                    defaultMessage: 'Select day',
                    description: 'Placeholder text when no check-in day is selected for hotel trip assignment',
                  })}
                </DaySelectButton>
                <ResponsiveDropdownList
                  open={isDayDropdownOpen}
                  onClose={handleDayDropdownClose}
                  triggerRef={dayButtonRef}
                  anchorRef={dayButtonRef}
                  size="S"
                >
                  {dayOptions.map(option => (
                    <ClickableListItem
                      key={option.dayNumber}
                      size="medium"
                      title={getLocalisedDayLabel(intl, option.dayNumber, option.date)}
                      subtitle={option.cityName}
                      data-day-number={option.dayNumber}
                      onClick={handleDaySelect}
                      selected={option.dayNumber === selectedDay?.dayNumber}
                    />
                  ))}
                </ResponsiveDropdownList>
              </FieldGroup>
            )}
            <NightsFieldGroup direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'Ryy4iNU6jcD3-4W9eFz1',
                  defaultMessage: 'Length of stay',
                  description: 'Label for the number of nights selection field when adding a hotel to trip',
                })}
              </BodyText>
              <StayCounter
                value={nights}
                min={nightsFloor}
                max={30}
                onChange={handleNightsChange}
                size="large"
                valueFormatter={nightsFormatter}
              />
            </NightsFieldGroup>
          </Group>
          {minimumStayNotice && (
            <BodyText variant="medium" colour="neutral-two">
              {minimumStayNotice}
            </BodyText>
          )}
        </Group>
      </Modal>

      <ExtendTripModal
        isOpen={extendState.isOpen}
        hotelName={hotelName}
        extraDays={extendState.extraDays}
        onClose={handleExtendClose}
        onConfirm={handleExtendConfirm}
      />
    </>
  )
}

export default AddHotelToTripModal
